import { IconButtonProps, InputProps, useNumberInput } from '@chakra-ui/react';
import { magnifyValue } from '@diamond/shared/decimal-util-web';
import {
  convertToFloat,
  numberWithSeparator,
  roundUpToMultiple,
  validateQuantityInput,
} from '@diamond/shared/utils';
import { useEffect, useMemo, useRef, useState } from 'react';

import { formatStockQty } from '../cart/format-stock-qty';

export interface UseStockNumberInputArgs {
  id: string;
  name: string;
  valueUi: number;
  stepUi: number;
  minUi: number;
  maxUi: number;
  stepMagnified?: number;
  isDisabled?: boolean;
  onChangeMagnified: (magnifiedQty: number) => void;
  onChangeMagnifiedImmediate?: (magnifiedQty: number) => void;
  onError?: (errorMessage: string) => void;
  debounceDelay?: number;
  roundOnBlurOnly?: boolean;
}

export interface UseStockNumberInputReturn {
  hasFractionalStep: boolean;
  fractionDigits: number;
  countDisplay: string;
  inputProps: Partial<InputProps>;
  incProps: Partial<IconButtonProps>;
  decProps: Partial<IconButtonProps>;
  getCurrentUiValue: () => number;
}

/**
 * Calculates the number of decimal places needed based on the step value.
 *
 * @param stepUi - The step value in UI format (decimal)
 * @returns The number of fraction digits (0-3)
 *
 * @example
 * getFractionDigits(1) // Returns: 0
 * getFractionDigits(0.5) // Returns: 1
 * getFractionDigits(0.25) // Returns: 2
 * getFractionDigits(0.125) // Returns: 3
 */
const getFractionDigits = (stepUi: number): number => {
  if (Number.isInteger(stepUi)) return 0;
  const decimals = stepUi.toString().split('.')[1]?.length ?? 2;
  return Math.min(decimals, 3);
};

/**
 * Creates a parser function for stock quantity input that enforces Indonesian number format.
 *
 * Indonesian number format:
 * - Dot (.) is used as thousand separator (e.g., 1.000, 100.000)
 * - Comma (,) is used as decimal separator (e.g., 1,5, 2,25)
 * - Example: "1.234,56" represents one thousand two hundred thirty-four point five six
 *
 * @param allowFraction - Whether fractional values are allowed
 * @param maxFractionDigits - Maximum number of decimal places (0-3)
 * @returns A parser function that normalizes input to standard format (dot as decimal separator)
 *
 * @example
 * const parseInteger = createStockInputParser(false, 0);
 * parseInteger('1.000') // Returns: '1000'
 * parseInteger('100,5') // Returns: '100' (fractions not allowed)
 *
 * @example
 * const parseFractional = createStockInputParser(true, 3);
 * parseFractional('1.234,56') // Returns: '1234.56'
 * parseFractional('100,5') // Returns: '100.5'
 * parseFractional('1.000') // Returns: '1000'
 */
export const createStockInputParser =
  (allowFraction: boolean, maxFractionDigits: number) =>
  (raw: string): string => {
    if (!raw || typeof raw !== 'string') {
      return '';
    }

    const trimmed = raw.trim();
    if (trimmed === '') {
      return '';
    }

    if (!allowFraction) {
      return trimmed.replace(/\D/g, '');
    }

    const sanitized = trimmed.replace(/[^0-9.,]/g, '');
    const commaIndex = sanitized.lastIndexOf(',');

    if (commaIndex === -1) {
      const result = sanitized.replace(/\./g, '');
      return result || '';
    }

    const integerPartRaw = sanitized.slice(0, commaIndex);
    const decimalPartRaw = sanitized.slice(commaIndex + 1);

    const integerDigits = integerPartRaw.replace(/\./g, '').replace(/,/g, '');
    const decimalDigits = decimalPartRaw.replace(/\D/g, '');

    if (decimalDigits.length === 0) {
      if (integerDigits.length === 0) {
        return '';
      }
      return integerDigits;
    }

    const limitedDecimal =
      maxFractionDigits > 0
        ? decimalDigits.slice(0, maxFractionDigits)
        : decimalDigits;

    const finalIntegerDigits = integerDigits || '0';
    return `${finalIntegerDigits}.${limitedDecimal}`;
  };

/**
 * Validates stock quantity value and returns validation result.
 *
 * @param value - The numeric value to validate
 * @param maxUi - Maximum allowed value in UI format
 * @returns Validation result with isValid flag and optional error message
 */
const validateStockQuantity = (
  value: number,
  maxUi: number
): { isValid: boolean; errorMessage?: string } => {
  if (Number.isNaN(value)) {
    return {
      isValid: false,
      errorMessage: 'Jumlah tidak valid. Silakan masukkan angka yang benar',
    };
  }

  const validation = validateQuantityInput(value, maxUi);
  return validation;
};

/**
 * Gets appropriate error message based on the error type.
 *
 * @param errorType - Type of error that occurred
 * @param maxUi - Maximum allowed value (for context in error messages)
 * @returns User-friendly error message in Indonesian
 */
const getStockInputErrorMessage = (
  errorType: 'invalid' | 'exceedsMax' | 'conversion',
  maxUi?: number
): string => {
  switch (errorType) {
    case 'invalid':
      return 'Jumlah tidak valid. Silakan masukkan angka yang benar';
    case 'exceedsMax':
      return 'Jumlah terlalu besar, menyesuaikan ke stok maksimum';
    case 'conversion':
      return 'Terjadi kesalahan saat memproses jumlah. Silakan coba lagi';
    default:
      return 'Jumlah tidak valid';
  }
};

/**
 * Handles value conversion from UI format to magnified format.
 *
 * @param valueUi - Value in UI format (decimal)
 * @returns Object containing magnified value
 * @throws Error if conversion fails
 */
const handleValueConversion = (valueUi: number): { magnifiedValue: number } => {
  const result = magnifyValue(valueUi, 'stock');
  return { magnifiedValue: result.magnifiedValue };
};

/**
 * Schedules a debounced callback execution.
 *
 * @param timeoutRef - Reference to store the timeout ID
 * @param callback - Function to execute after delay
 * @param delay - Delay in milliseconds
 */
const scheduleDebouncedCallback = (
  timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
  callback: () => void,
  delay: number
): void => {
  if (timeoutRef.current) {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }

  timeoutRef.current = setTimeout(() => {
    callback();
    timeoutRef.current = null;
  }, delay);
};

/**
 * Validates and converts effective value to magnified format.
 *
 * @param effectiveValue - The effective value (already clamped to max if needed)
 * @param maxUi - Maximum allowed value in UI format
 * @param onError - Optional error callback
 * @returns Object with magnified value, or null if validation fails
 */
const validateAndConvertValue = (
  effectiveValue: number,
  maxUi: number,
  onError?: (errorMessage: string) => void
): { magnifiedValue: number } | null => {
  const validation = validateStockQuantity(effectiveValue, maxUi);
  if (!validation.isValid) {
    if (onError) {
      onError(validation.errorMessage || getStockInputErrorMessage('invalid'));
    }
    return null;
  }

  try {
    const { magnifiedValue } = handleValueConversion(effectiveValue);
    return { magnifiedValue };
  } catch (error) {
    if (onError) {
      onError(getStockInputErrorMessage('conversion'));
    }
    return null;
  }
};

/**
 * Handles stock quantity input with validation and conversion between UI (decimal) and magnified (x1000) formats.
 *
 * This hook manages stock quantity input with the following features:
 * - Supports both integer and fractional step quantities
 * - Enforces Indonesian number format (comma for decimals, dot for thousands)
 * - Provides immediate UI updates and debounced API persistence
 * - Validates input against min/max constraints
 * - Converts between UI format (decimal) and magnified format (integer x1000)
 *
 * @param args - Configuration object for the hook
 * @param args.id - HTML id attribute for the input element
 * @param args.name - HTML name attribute for the input element
 * @param args.valueUi - Current value in UI format (decimal, e.g., 1.5)
 * @param args.stepUi - Step increment in UI format (decimal, e.g., 0.5)
 * @param args.minUi - Minimum allowed value in UI format
 * @param args.maxUi - Maximum allowed value in UI format
 * @param args.isDisabled - Whether the input is disabled
 * @param args.onChangeMagnified - Callback called after debounce with magnified value (x1000)
 * @param args.onChangeMagnifiedImmediate - Optional callback called immediately with magnified value for optimistic UI updates
 * @param args.onError - Optional callback for error messages
 * @param args.debounceDelay - Delay in milliseconds for debounced callback (default: 1000)
 *
 * @returns Object containing:
 * - hasFractionalStep: Whether the step allows fractional values
 * - fractionDigits: Number of decimal places (0-3)
 * - countDisplay: Formatted display string for the quantity
 * - inputProps: Props to spread on input element
 * - incProps: Props to spread on increment button
 * - decProps: Props to spread on decrement button
 * - getCurrentUiValue: Function to get current UI value
 *
 * @example
 * ```tsx
 * const {
 *   countDisplay,
 *   inputProps,
 *   incProps,
 *   decProps,
 * } = useStockNumberInput({
 *   id: 'quantity-input',
 *   name: 'quantity',
 *   valueUi: 1.5,
 *   stepUi: 0.5,
 *   minUi: 0.5,
 *   maxUi: 100,
 *   onChangeMagnified: (magnified) => {
 *     // Called after 1000ms debounce
 *     updateCart(magnified); // magnified = 1500
 *   },
 *   onChangeMagnifiedImmediate: (magnified) => {
 *     // Called immediately
 *     updateUI(magnified); // magnified = 1500
 *   },
 *   onError: (message) => {
 *     toast.error(message);
 *   },
 * });
 * ```
 *
 * @remarks
 * - UI values are displayed as decimals (e.g., 1.5)
 * - Magnified values are integers multiplied by 1000 (e.g., 1500)
 * - Input format follows Indonesian convention: comma for decimals, dot for thousands
 * - Values are automatically clamped to min/max range on blur
 */
export const useStockNumberInput = (
  args: UseStockNumberInputArgs
): UseStockNumberInputReturn => {
  const {
    id,
    name,
    valueUi,
    stepUi,
    minUi,
    maxUi,
    stepMagnified,
    isDisabled,
    onChangeMagnified,
    onChangeMagnifiedImmediate,
    onError,
    debounceDelay = 1000,
    roundOnBlurOnly = false,
  } = args;

  const normalizedValueUi = useMemo(() => {
    /**
     * Defense-in-depth: never allow non-finite values to enter Chakra's `useNumberInput` state.
     *
     * The intended invariant for this hook is: `valueUi` and `minUi` are finite numbers.
     * In practice, upstream props can momentarily become invalid (bugs, stale cache, races),
     * and Chakra/React will happily render "NaN" which is a bad UX.
     */
    if (Number.isFinite(valueUi)) return valueUi;
    if (Number.isFinite(minUi)) return minUi;
    return 0;
  }, [valueUi, minUi]);

  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isUserModifiedRef = useRef<boolean>(false);
  const [uiValueState, setUIValueState] = useState<number>(normalizedValueUi);
  const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
  const prevValueUiRef = useRef<number>(normalizedValueUi);

  useEffect(() => {
    // Sync internal state with external valueUi when it changes externally
    if (prevValueUiRef.current !== normalizedValueUi) {
      // If user is not actively typing, always sync
      if (!isInputFocused || !isUserModifiedRef.current) {
        setUIValueState(normalizedValueUi);
      }
      prevValueUiRef.current = normalizedValueUi;
    }
  }, [normalizedValueUi, isInputFocused]);

  const fractionDigits = useMemo(() => getFractionDigits(stepUi), [stepUi]);
  const hasFractionalStep = fractionDigits > 0;

  const parse = useMemo(
    () => createStockInputParser(hasFractionalStep, fractionDigits),
    [hasFractionalStep, fractionDigits]
  );

  const handleInputChange = (val: string): void => {
    const numeric = convertToFloat(val);

    if (Number.isNaN(numeric)) {
      if (onError) {
        onError(getStockInputErrorMessage('invalid'));
      }
      return;
    }

    const isExceedingMax = numeric > maxUi;
    const effectiveValue = isExceedingMax ? maxUi : numeric;

    isUserModifiedRef.current = true;
    setUIValueState(effectiveValue);

    const conversionResult = validateAndConvertValue(
      effectiveValue,
      maxUi,
      onError
    );
    if (!conversionResult) {
      return;
    }

    const { magnifiedValue } = conversionResult;

    if (onChangeMagnifiedImmediate) {
      onChangeMagnifiedImmediate(magnifiedValue);
    }

    if (isExceedingMax && onError) {
      onError(getStockInputErrorMessage('exceedsMax'));
    }

    let finalMagnified = magnifiedValue;
    if (!roundOnBlurOnly && stepMagnified && stepMagnified > 0) {
      finalMagnified = roundUpToMultiple(magnifiedValue, stepMagnified);
    }

    scheduleDebouncedCallback(
      validationTimeoutRef,
      () => onChangeMagnified(finalMagnified),
      debounceDelay
    );
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
    isUserModifiedRef.current = false;

    // Clear any pending debounced updates so they don't overwrite the blur rounding
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }

    if (stepMagnified && stepMagnified > 0) {
      if (Number.isNaN(uiValueState)) {
        setUIValueState(normalizedValueUi);
        return;
      }
      const { magnifiedValue } = handleValueConversion(uiValueState);
      const roundedMagnified = roundUpToMultiple(magnifiedValue, stepMagnified);
      if (
        Number.isFinite(roundedMagnified) &&
        roundedMagnified !== magnifiedValue
      ) {
        onChangeMagnified(roundedMagnified);
        // Immediately update local state to the rounded value (e.g., 10.92)
        // This prevents the UI from resetting to un-rounded '10' prematurely
        setUIValueState(roundedMagnified / 1000);
        return;
      }
    }

    // Ensure we match the parent's source of truth after blurring and potentially rounding
    setUIValueState(normalizedValueUi);
  };

  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      id,
      name,
      value: uiValueState,
      step: stepUi,
      min: minUi,
      precision: fractionDigits,
      max: maxUi,
      isDisabled,
      parse,
      format: (val) => numberWithSeparator(val),
      keepWithinRange: true,
      clampValueOnBlur: true,
      onChange: handleInputChange,
    });

  // Enhance input props with focus tracking
  const enhancedInputProps = {
    ...getInputProps(),
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      getInputProps().onFocus?.(e);
      handleInputFocus();
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      getInputProps().onBlur?.(e);
      handleInputBlur();
    },
  };

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }
    };
  }, []);

  const inputProps = enhancedInputProps;
  const incProps = getIncrementButtonProps();
  const decProps = getDecrementButtonProps();

  const currentUiValue = uiValueState;
  const countDisplay = useMemo(
    () => formatStockQty(currentUiValue, { fractionDigits }),
    [currentUiValue, fractionDigits]
  );

  return {
    hasFractionalStep,
    fractionDigits,
    countDisplay,
    inputProps,
    incProps,
    decProps,
    getCurrentUiValue: () => currentUiValue,
  };
};
